import { useNavigate } from 'react-router-dom';
import { useTour, type TourType } from '@/context/TourContext';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClipboardList, Contact, X } from 'lucide-react';

export default function TourChoiceDialog() {
  const { showChoiceDialog, startTour, dismissChoice } = useTour();
  const { user } = useAuth();
  const navigate = useNavigate();

  const isSuperAdmin = user?.is_super_admin ?? false;
  const crmEnabled = isSuperAdmin || (user?.tenant?.features?.crm_enabled ?? false);

  const handleStart = (type: TourType) => {
    if (type === 'crm') {
      navigate('/crm');
    } else {
      navigate('/dashboard');
    }
    startTour(type);
  };

  return (
    <Dialog open={showChoiceDialog} onOpenChange={(open) => !open && dismissChoice()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">Welcome, {user?.name?.split(' ')[0] || 'there'}! 👋</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Would you like a quick guided tour? Choose a module to get started.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-4">
          <button
            onClick={() => handleStart('work-orders')}
            className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <ClipboardList className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Work Order Walkthrough</p>
              <p className="text-sm text-muted-foreground">
                Learn how to manage pickups, intake, processing, and compliance.
              </p>
            </div>
          </button>

          {crmEnabled && (
            <button
              onClick={() => handleStart('crm')}
              className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Contact className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">CRM Walkthrough</p>
                <p className="text-sm text-muted-foreground">
                  Explore customers, contacts, deals, and appointments.
                </p>
              </div>
            </button>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={dismissChoice} className="text-muted-foreground">
            Skip for now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
